/**
 * Centralized Guardian Configuration
 * Single source of truth for all guardian data across the application
 */

// Import all guardian images
import steriImg from '@/assets/images/patty/guardian/steri.svg';
import handHygieneImg from '@/assets/images/patty/guardian/hand-hygenie.svg';
import environmentImg from '@/assets/images/patty/guardian/environment.svg';
import healthSafetyImg from '@/assets/images/patty/guardian/health-safety.svg';
import constructionImg from '@/assets/images/patty/guardian/construction.svg';
import dataAnalysisImg from '@/assets/images/patty/guardian/data-analysis.svg';
import educationImg from '@/assets/images/patty/guardian/education.svg';
import mifuImg from '@/assets/images/patty/guardian/mifu.svg';
import utiImg from '@/assets/images/patty/guardian/uti.svg';
import outbreakImg from '@/assets/images/patty/guardian/outbreak.svg';
import immunizationImg from '@/assets/images/patty/guardian/immunization.svg';

// Import wave guardian images
import steriWaveImg from '@/assets/images/patty/guardian/ster-wave.svg';
import handHygieneWaveImg from '@/assets/images/patty/guardian/hand-hygenie-wave.svg';
import environmentWaveImg from '@/assets/images/patty/guardian/environment-wave.svg';
import healthSafetyWaveImg from '@/assets/images/patty/guardian/health-safety-wave.svg';
import constructionWaveImg from '@/assets/images/patty/guardian/construction-wave.svg';
import dataAnalysisWaveImg from '@/assets/images/patty/guardian/data-analysis-wave.svg';
import educationWaveImg from '@/assets/images/patty/guardian/education-wave.svg';
import mifuWaveImg from '@/assets/images/patty/guardian/mifu-wave.svg';
import utiWaveImg from '@/assets/images/patty/guardian/uti-wave.svg';
import outbreakWaveImg from '@/assets/images/patty/guardian/outbreak-wave.svg';
import immunizationWaveImg from '@/assets/images/patty/guardian/immunization-wave.svg';

/**
 * Guardian agent types temporarily hidden from selection panels.
 * Remove entries here to re-enable an assistant.
 */
export const DISABLED_GUARDIAN_AGENT_TYPES = [
    'IMMUNIZATION',
    'DATA_ANALYSIS',
    'CONSTRUCTION',
];

/**
 * Guardian definitions with all properties
 * @property {string} id - Unique identifier
 * @property {string} name - Guardian's name
 * @property {string} agentType - Backend agent type
 * @property {*} image - Imported image asset
 * @property {*} waveImage - Imported wave image asset for chat view
 * @property {string} imagePath - Relative path to image
 * @property {string[]} keywords - Keywords for suggestion matching
 */
export const GUARDIANS = [
    {
        id: 'hand-hygiene',
        name: 'Finn',
        agentType: 'HAND_HYGIENE',
        image: handHygieneImg,
        waveImage: handHygieneWaveImg,
        imagePath: 'patty/guardian/hand-hygenie.svg',
        keywords: [
            'hand hygiene',
            'handwash',
            'hand wash',
            'sanitizer',
            'glove',
        ],
    },
    {
        id: 'environmental',
        name: 'Max',
        agentType: 'ENVIRONMENTAL',
        image: environmentImg,
        waveImage: environmentWaveImg,
        imagePath: 'patty/guardian/environment.svg',
        keywords: ['environment', 'cleaning', 'surface'],
    },
    {
        id: 'sterilization',
        name: 'Juno',
        agentType: 'STERILIZATION',
        image: steriImg,
        waveImage: steriWaveImg,
        imagePath: 'patty/guardian/steri.svg',
        keywords: ['steril', 'autoclave', 'instrument', 'disinfection'],
    },
    {
        id: 'outbreak',
        name: 'Niko',
        agentType: 'OUTBREAK',
        image: outbreakImg,
        waveImage: outbreakWaveImg,
        imagePath: 'patty/guardian/outbreak.svg',
        keywords: [
            'outbreak',
            'epidemic',
            'cluster',
            'cohorting',
            'isolation',
            'containment',
            'reporting',
            'disease spread',
        ],
    },
    {
        id: 'health-safety',
        name: 'Astra',
        agentType: 'HEALTH_SAFETY',
        image: healthSafetyImg,
        waveImage: healthSafetyWaveImg,
        imagePath: 'patty/guardian/health-safety.svg',
        keywords: ['health', 'safety', 'ppe', 'protective equipment'],
    },
    {
        id: 'education',
        name: 'Mira',
        agentType: 'EDUCATION',
        image: educationImg,
        waveImage: educationWaveImg,
        imagePath: 'patty/guardian/education.svg',
        keywords: [
            'education',
            'training',
            'learning',
            'course',
            'competency',
            'onboarding',
            'certification',
            'teach',
        ],
    },
    {
        id: 'uti',
        name: 'Nova',
        agentType: 'UTI',
        image: utiImg,
        waveImage: utiWaveImg,
        imagePath: 'patty/guardian/uti.svg',
        keywords: [
            'uti',
            'urinary tract infection',
            'catheter',
            'foley',
            'bladder',
            'infection',
            'urine',
            'surveillance',
        ],
    },
    {
        id: 'immunization',
        name: 'Echo',
        agentType: 'IMMUNIZATION',
        image: immunizationImg,
        waveImage: immunizationWaveImg,
        imagePath: 'patty/guardian/immunization.svg',
        keywords: [
            'immunization',
            'vaccination',
            'vaccine',
            'flu shot',
            'influenza',
            'immunize',
            'inoculation',
            'shot',
        ],
    },
    {
        id: 'data-analysis',
        name: 'Quincy',
        agentType: 'DATA_ANALYSIS',
        image: dataAnalysisImg,
        waveImage: dataAnalysisWaveImg,
        imagePath: 'patty/guardian/data-analysis.svg',
        keywords: [
            'data analysis',
            'analysis',
            'documentation',
            'logs',
            'audit',
            'inspection',
            'retention',
            'evidence',
        ],
    },
    {
        id: 'mifu',
        name: 'Amber',
        agentType: 'MIFU',
        image: mifuImg,
        waveImage: mifuWaveImg,
        imagePath: 'patty/guardian/mifu.svg',
        keywords: [
            'mifu',
            'manufacturer instructions for use',
            'instructions for use',
            'manufacturer guidance',
            'equipment',
            'product',
            'cleaning',
            'maintenance',
        ],
    },
    {
        id: 'construction',
        name: 'Atlas',
        agentType: 'CONSTRUCTION',
        image: constructionImg,
        waveImage: constructionWaveImg,
        imagePath: 'patty/guardian/construction.svg',
        keywords: ['construction', 'renovation', 'building', 'dust'],
    },
];

/**
 * Get guardian by ID
 * @param {string} id - Guardian ID
 * @returns {Object|undefined} Guardian object or undefined if not found
 */
export const getGuardianById = (id) => {
    if (!id) return undefined;
    return GUARDIANS.find((guardian) => guardian.id === id);
};

/**
 * Get guardian by agent type
 * @param {string} agentType - Agent type (e.g., 'HAND_HYGIENE')
 * @returns {Object|undefined} Guardian object or undefined if not found
 */
export const getGuardianByAgentType = (agentType) => {
    if (!agentType) return undefined;
    return GUARDIANS.find((guardian) => guardian.agentType === agentType);
};

/**
 * Find suggested guardian based on query keywords
 * @param {string} queryText - Query text to match against keywords
 * @param {string} currentAgentType - Current guardian's agent type to exclude
 * @returns {Object} Suggested guardian or Patty as fallback
 */
export const findSuggestedGuardian = (queryText, currentAgentType = null) => {
    // Handle undefined/null queryText
    if (!queryText || typeof queryText !== 'string') {
        return (
            getGuardianByAgentType('PATTY') || {
                id: 'all-topics',
                name: 'Patty',
                agentType: 'PATTY',
            }
        );
    }

    const query = queryText.toLowerCase();

    // Find guardian that matches keywords and is NOT the current guardian
    const matchedGuardian = GUARDIANS.find(
        (g) =>
            g.agentType !== currentAgentType &&
            !DISABLED_GUARDIAN_AGENT_TYPES.includes(g.agentType) &&
            g.keywords &&
            Array.isArray(g.keywords) &&
            g.keywords.some((keyword) => keyword && query.includes(keyword))
    );

    // If found a match, return it. Otherwise return Patty (All Topics) as fallback
    return (
        matchedGuardian ||
        getGuardianByAgentType('PATTY') || {
            id: 'all-topics',
            name: 'Patty',
            agentType: 'PATTY',
        }
    );
};
